import { API_AUTH_TOKEN } from "@iwoca/lapi-client/lendingAPI";

import logger from "../../utils/logger";

/**
 * Custom fetch function for email availability that includes authorization header
 * This is needed because the generated lapi-client fetchers don't include the auth token
 */
async function getEmailAvailability(
  email: string,
): Promise<{ data: { available: boolean } }> {
  const encodedEmail = encodeURIComponent(email);
  const response = await fetch(
    `/api/lending/edge/email_availability/?email=${encodedEmail}`,
    {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        authorization: API_AUTH_TOKEN,
      },
    },
  );

  if (!response.ok) {
    const text = await response.text();
    const error: any = new Error(text);
    error.status = response.status;
    error.response = { status: response.status };
    throw error;
  }

  return await response.json();
}

/**
 * Check if an email exists in the system
 * This helps determine whether to use Django magic link or Cognito magic link
 *
 * @param email - The email address to check
 * @returns Promise<boolean> - true if email exists in Django/legacy system, false if potentially new Cognito user
 *
 * Uses the existing email_availability endpoint which returns:
 * - { available: true } if email doesn't exist (can be used for new signup)
 * - { available: false } if email exists (user should login)
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const response = await getEmailAvailability(email);
    // If email is NOT available, it means it exists in the system
    return response.data.available === false;
  } catch (error: any) {
    logger.error("Error checking email existence", {
      error: error?.message || "Unknown error",
      status: error?.status || "unknown",
    });
    // On errors, default to assuming email exists (use Django flow as fallback)
    // This is safer as it won't break existing functionality
    return true;
  }
}

/**
 * Determine which authentication flow to use based on email existence
 *
 * @param email - The email address to check
 * @returns Promise<'django' | 'auth-service'> - Which flow to use
 *
 * Logic:
 * - If email exists in Django/legacy system -> use Django magic link
 * - If email doesn't exist in Django -> use auth-service (new user via Cognito backend)
 */
export async function determineAuthFlow(
  email: string,
): Promise<"django" | "auth-service"> {
  const existsInDjango = await checkEmailExists(email);

  if (existsInDjango) {
    logger.info("Determining auth flow", { flowType: "django" });
    return "django";
  } else {
    logger.info("Determining auth flow", { flowType: "auth-service" });
    return "auth-service";
  }
}
